/**
 * peptideOrder.ts
 *
 * Shared logic for Axiom peptide orders: computing when a patient will run out
 * of supply (so we can prompt a re-order a week early), and rebuilding a create
 * payload from an existing order so a "Re-order" re-runs it.
 *
 * Used by both the per-patient Peptides sub-tab and the global Peptides page.
 */
import type { IntegrationOrder, OrderItemSelection } from '@/services/integrations.service';

/**
 * How long an order waits before it goes to the pharmacy, in words.
 *
 * The wait is an environment setting, not a constant: staging runs it at 0 so a
 * test order can be followed through end to end (SCRUM-1661). Hardcoding "~1h"
 * there would tell someone to wait for something that already happened.
 *
 * Lives here rather than beside the labels because both the per-patient tab and
 * the order flow need it, and the tab already imports the flow — putting it in
 * either would make that a cycle.
 */
export function submitDelayPhrase(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return 'shortly';
  if (minutes <= 0) return 'within a minute';
  if (minutes < 60) return `in ~${minutes}m`;
  return `in ~${Math.round(minutes / 60)}h`;
}

/** Prompt the re-order this many days before the estimated supply runs out. */
const REORDER_LEAD_DAYS = 7;
/** Fallback supply when a product has no per-cartridge duration: one month. */
const FALLBACK_WEEKS_PER_CART = 4;
const MS_PER_DAY = 86_400_000;

interface OrderLine {
  integrationProductId?: number;
  quantity?: number;
  productMetadata?: Record<string, unknown> | null;
}

function lineItemsOf(order: IntegrationOrder): OrderLine[] {
  const meta = (order.metadata ?? {}) as Record<string, unknown>;
  const items = meta.lineItems;
  return Array.isArray(items) ? (items as OrderLine[]) : [];
}

/** Shortest prescription day supply trusted as real (mirrors MIN_RX_SUPPLY_DAYS). */
const MIN_RX_SUPPLY_DAYS = 7;

/**
 * Days of supply of a prescription order: undefined when it is not one (no
 * line carries a daySupply), null when it is but no script's supply is usable.
 */
function prescriptionSupplyDays(order: IntegrationOrder): number | null | undefined {
  const scripts = (order.items ?? []).filter((i) => i.daySupply != null && String(i.daySupply).trim() !== '');
  if (scripts.length === 0) return undefined;
  const days = scripts
    .map((i) => parseInt(String(i.daySupply).trim().replace(/\D.*$/, ''), 10))
    .filter((d) => Number.isFinite(d) && d > 0);
  const max = days.length > 0 ? Math.max(...days) : null;
  return max !== null && max >= MIN_RX_SUPPLY_DAYS ? max : null;
}

/** Parse the injections-per-week out of a "5x/week" style frequency string. */
function injectionsPerWeek(freq: unknown): number | null {
  if (typeof freq !== 'string') return null;
  const m = freq.match(/(\d+(?:\.\d+)?)\s*x?\s*\/?\s*week/i);
  return m ? Number(m[1]) : null;
}

/** Weeks of supply an order provides, and whether that number is a guess. */
interface SupplyEstimate {
  weeks: number;
  /** True when no line carried a real duration and FALLBACK_WEEKS_PER_CART stood in. */
  estimated: boolean;
}

/**
 * Weeks of supply one order provides = the longest-lasting line's duration.
 * Per line: prefer the precomputed weeksPerCart, else derive it from
 * injectionsPerCart ÷ injectionsPerWeek(frequency); multiply by the ordered
 * cartridge quantity. Falls back to a one-month supply when nothing is known.
 *
 * Tracks whether the winning line actually knew its duration, because the
 * fallback is a guess and the ECCs plan refills off this number — a guess has
 * to be labelled as one rather than shown as fact.
 */
function computeSupply(order: IntegrationOrder): SupplyEstimate {
  // A prescription order (Partell) carries each script's own day supply, not
  // catalog cartridges. Same rule as the server's reorder email
  // (IntegrationOrderNotifier::supplyDays): the longest script, and under a
  // week is not trusted. Unknown falls through to the labelled estimate.
  const rxDays = prescriptionSupplyDays(order);
  if (rxDays !== undefined) {
    return rxDays !== null
      ? { weeks: rxDays / 7, estimated: false }
      : { weeks: FALLBACK_WEEKS_PER_CART, estimated: true };
  }

  let best = 0;
  let bestKnown = false;
  for (const line of lineItemsOf(order)) {
    const pm = line.productMetadata ?? {};
    const qty = typeof line.quantity === 'number' && line.quantity > 0 ? line.quantity : 1;
    let weeksPerCart: number | null =
      typeof pm.weeksPerCart === 'number' && pm.weeksPerCart > 0 ? pm.weeksPerCart : null;
    if (weeksPerCart == null) {
      const inj = typeof pm.injectionsPerCart === 'number' ? pm.injectionsPerCart : null;
      const perWk = injectionsPerWeek(pm.frequency);
      if (inj != null && perWk && perWk > 0) weeksPerCart = inj / perWk;
    }
    // A supplies-only line (pen/needles, no dosage) contributes no duration.
    if (weeksPerCart == null && (pm.isAddOn || pm.addOnType)) continue;
    const lineWeeks = (weeksPerCart ?? FALLBACK_WEEKS_PER_CART) * qty;
    if (lineWeeks > best) {
      best = lineWeeks;
      bestKnown = weeksPerCart != null;
    }
  }
  return best > 0
    ? { weeks: best, estimated: !bestKnown }
    : { weeks: FALLBACK_WEEKS_PER_CART, estimated: true };
}

export function supplyWeeks(order: IntegrationOrder): number {
  return computeSupply(order).weeks;
}

/**
 * Days of supply an order provides — the unit the ECC spreadsheet tracks, and
 * the one the orders list shows.
 */
export function supplyDays(order: IntegrationOrder): number {
  return Math.round(computeSupply(order).weeks * 7);
}

/**
 * Whether the supply figure is the fallback rather than a real per-cartridge
 * duration. The number is still shown — an estimate beats a dash for the ECCs —
 * but the cell marks it so nobody schedules a refill around a guess.
 */
export function supplyIsEstimated(order: IntegrationOrder): boolean {
  return computeSupply(order).estimated;
}

/**
 * The date a re-order should be placed: supply starts at delivery, so measure
 * from the ship date when known (else the order-created date), add the supply
 * duration, and subtract the lead time. Null when neither date is available.
 */
export function reorderDate(order: IntegrationOrder): Date | null {
  const meta = (order.metadata ?? {}) as Record<string, unknown>;
  const tracking = (meta.tracking ?? {}) as Record<string, unknown>;
  const anchorIso =
    (typeof tracking.shippedAt === 'string' && tracking.shippedAt) ||
    order.createdAt ||
    null;
  if (!anchorIso) return null;
  const anchor = new Date(anchorIso);
  if (Number.isNaN(anchor.getTime())) return null;

  const supplyMs = supplyWeeks(order) * 7 * MS_PER_DAY;
  return new Date(anchor.getTime() + supplyMs - REORDER_LEAD_DAYS * MS_PER_DAY);
}

/**
 * Whether an order is finished, so it can be re-ordered: delivered (Axiom), or
 * completed (Axiom's terminal state, and Partell's "Completed" — the pharmacy
 * filled and shipped it). Anything earlier is still in flight.
 */
export function isReorderable(order: IntegrationOrder): boolean {
  return order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'completed';
}

/** Whether the re-order window has arrived (the re-order date is today or past). */
export function isReorderDue(order: IntegrationOrder, now: number = Date.now()): boolean {
  const d = reorderDate(order);
  return d != null && d.getTime() <= now;
}

/** Midnight local time — so "days away" counts calendar days, not elapsed ms. */
function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * The re-order date plus how far off it is, for a cell that has to answer
 * "when do I re-order this?" rather than just print a date.
 *
 * `daysAway` counts CALENDAR days: an order due today reads "Due today" all day
 * instead of flipping to "1 day overdue" a millisecond after midnight. `due`
 * stays delegated to isReorderDue() so the row highlight and the page's
 * "re-order due" KPI tile can never disagree about what counts as due.
 */
export function reorderCountdown(
  order: IntegrationOrder,
  now: number = Date.now(),
): { date: Date; daysAway: number; due: boolean } | null {
  const date = reorderDate(order);
  if (!date) return null;
  return {
    date,
    daysAway: Math.round((startOfDay(date) - startOfDay(new Date(now))) / MS_PER_DAY),
    due: isReorderDue(order, now),
  };
}

/**
 * The dosed (non-add-on) products an order carries, as a stable, order-
 * independent key — sorted so the same drug combo always keys the same
 * regardless of which line came first. `null` when an order has no dosed
 * line at all (nothing to chain on).
 */
function reorderChainKey(order: IntegrationOrder): string | null {
  // A prescription order (Partell) has no catalog lines: its scripts are the
  // products, so the chain is the same patient re-ordering the same drug(s).
  if (prescriptionSupplyDays(order) !== undefined) {
    const drugs = (order.items ?? []).map((i) => (i.name ?? '').trim().toLowerCase()).filter(Boolean);
    return drugs.length > 0 ? `rx:${[...new Set(drugs)].sort().join('|')}` : null;
  }
  const ids = lineItemsOf(order)
    .filter((l) => {
      const pm = (l.productMetadata ?? {}) as Record<string, unknown>;
      return !pm.isAddOn && !pm.addOnType;
    })
    .map((l) => l.integrationProductId)
    .filter((id): id is number => typeof id === 'number');
  if (ids.length === 0) return null;
  return [...new Set(ids)].sort((a, b) => a - b).join(',');
}

/**
 * An order that will never arrive: cancelled (either field), or failed /
 * rejected / errored at the vendor (Axiom's failed and error, a Partell
 * script the pharmacy rejected). It re-orders nothing, so it never
 * supersedes the order it was placed from — that one keeps its Re-order.
 * An unpaid order still counts: it can be paid and go out.
 */
const DEAD_FULFILLMENT = new Set(['cancelled', 'failed', 'rejected', 'error']);
function isCancelledOrder(order: IntegrationOrder): boolean {
  return order.fulfillmentStatus === 'cancelled' || order.status === 'cancelled';
}
export function isDeadOrder(order: IntegrationOrder): boolean {
  return order.status === 'cancelled' || DEAD_FULFILLMENT.has(order.fulfillmentStatus ?? '');
}

/**
 * Whether an order counts as "Due for Re-order": its window has arrived AND the
 * table would show it a Re-order button (finished, not dead, not already
 * re-ordered). The Peptides and Partell tiles and filters use this so they
 * count what the table offers.
 */
export function isDueForReorder(order: IntegrationOrder, supersededBy: Map<number, unknown>, now: number = Date.now()): boolean {
  return isReorderDue(order, now) && isReorderable(order) && !isDeadOrder(order) && !supersededBy.has(order.integrationOrderId);
}

/**
 * Which order, if any, already re-ordered each order in the list.
 *
 * A reorder chain is every order for the SAME patient carrying the SAME dosed
 * product(s) (see reorderChainKey — add-ons are excluded so a pen riding
 * along with different peptides never falsely merges two unrelated chains).
 * Within a chain, only the newest order that can still arrive (not
 * isDeadOrder) has a live
 * reorder-due state; every earlier order in that chain was already followed
 * by a real order, so its own overdue math would double-prompt for
 * something already done.
 *
 * Returns a Map from integrationOrderId to the id of the order that
 * superseded it — present only for orders that are NOT the newest in their
 * chain. `patientOf` lets a single-patient list (which does not carry
 * `patientId` per row) supply a constant; the account-wide list already has
 * a real `patientId` on every row.
 */
export function computeSupersededBy(
  orders: IntegrationOrder[],
  patientOf: (order: IntegrationOrder) => number | string = () => 0,
): Map<number, number> {
  const chains = new Map<string, IntegrationOrder[]>();
  for (const o of orders) {
    // Cancelled orders stay out of chains entirely; a failed / rejected one is
    // a member (a later live order supersedes it) but never the newest.
    if (isCancelledOrder(o)) continue;
    const productKey = reorderChainKey(o);
    if (productKey === null) continue;
    const key = `${patientOf(o)}|${productKey}`;
    (chains.get(key) ?? chains.set(key, []).get(key)!).push(o);
  }

  const supersededBy = new Map<number, number>();
  // An explicit Re-order link wins: the order a re-order was placed FROM is
  // done, even when the new order changed its items and so is not in its
  // product chain. Applied first; a newer chain member still overrides below.
  for (const o of orders) {
    if (isDeadOrder(o)) continue;
    const fromId = Number((o.metadata as Record<string, unknown> | null)?.reorderOfOrderId ?? 0);
    if (fromId > 0 && fromId !== o.integrationOrderId) supersededBy.set(fromId, o.integrationOrderId);
  }
  for (const chain of chains.values()) {
    if (chain.length < 2) continue;
    // Newest first — createdAt is the actual placement time; integrationOrderId
    // is a stable, always-present tiebreaker for same-instant edge cases.
    const sorted = [...chain].sort((a, b) => {
      const byDate = new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
      return byDate !== 0 ? byDate : b.integrationOrderId - a.integrationOrderId;
    });
    const newestAt = sorted.findIndex((o) => !isDeadOrder(o));
    if (newestAt < 0) continue;
    const newest = sorted[newestAt];
    for (const o of sorted.slice(newestAt + 1)) {
      supersededBy.set(o.integrationOrderId, newest.integrationOrderId);
    }
  }
  return supersededBy;
}

/**
 * Rebuild the create payload (items + metadata) from an existing order so a
 * re-order re-runs the same prescription. Returns null if the order has no
 * line items to reconstruct from.
 */
export function reorderPayload(
  order: IntegrationOrder,
): { items: OrderItemSelection[]; metadata: Record<string, unknown> } | null {
  const lines = lineItemsOf(order);
  const items: OrderItemSelection[] = lines
    .filter((l) => typeof l.integrationProductId === 'number')
    .map((l) => ({
      integrationProductId: l.integrationProductId as number,
      quantity: typeof l.quantity === 'number' && l.quantity > 0 ? l.quantity : 1,
    }));
  if (items.length === 0) return null;

  // Carry the prescription context forward; drop the prior shipment/tracking so
  // the new order starts clean.
  const meta = { ...((order.metadata ?? {}) as Record<string, unknown>) };
  delete meta.tracking;
  delete meta.axiomOrderId;
  delete meta.partnerOrderId;
  return { items, metadata: meta };
}
